import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  Column,
  ColumnGroup,
  HeaderClass,
  HeaderClassParams,
  IAggFuncParams,
} from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import { clsx } from "clsx";
import Decimal from "decimal.js";
import { isFunction } from "lodash-es";
import { formatMoneyWithoutCurrencySymbol } from "shared/utils/MoneyUtils";

export const maybernTableDecimalSumAggFunc = (arg: IAggFuncParams): string => {
  const totalDecimal: Decimal = (arg.values as Decimal[]).reduce(
    (a: Decimal, b: Decimal | undefined) => a.plus(b ?? new Decimal("0")),
    new Decimal("0"),
  );
  return formatMoneyWithoutCurrencySymbol(totalDecimal.toString());
};

export const isMaybernTableColumnLastInGroup = (
  params: HeaderClassParams | CellClassParams,
) => {
  const colIndex = params.api.getAllDisplayedColumns().indexOf(params.column!);
  const nextCol = params.api.getAllDisplayedColumns()[colIndex + 1] as
    | Column
    | undefined;
  return (
    nextCol?.getColId() === "spacer" ||
    (params.column?.getParent() as ColumnGroup | null)?.getGroupId() !==
      (nextCol?.getParent() as ColumnGroup | null)?.getGroupId()
  );
};

export const maybernTableMergeHeaderClass =
  (headerClass: HeaderClass) => (params: HeaderClassParams) => {
    const headerClassValue = isFunction(headerClass)
      ? headerClass(params)
      : headerClass;
    const defaultColDef = params.api.getGridOption("defaultColDef");
    const defaultHeaderClass = defaultColDef?.headerClass;
    const defaultHeaderClassValue = isFunction(defaultHeaderClass)
      ? defaultHeaderClass(params)
      : defaultHeaderClass;
    return clsx(defaultHeaderClassValue, headerClassValue);
  };

export const isColGroupDefWithChildren = (
  c: ColDef | ColGroupDef | null,
): c is ColGroupDef =>
  Boolean(
    c &&
      "children" in c &&
      (c.children as (ColDef | ColGroupDef)[] | undefined)?.length,
  );

export const isColDef = (colDef: ColDef | ColGroupDef): colDef is ColDef =>
  "field" in colDef;

export const isGroupColDef = (
  colDef: ColDef | ColGroupDef,
): colDef is ColGroupDef =>
  "children" in colDef &&
  (colDef.children as (ColDef | ColGroupDef)[] | undefined) !== undefined;

export const flattenColDefs = <TData>(
  defs: (ColDef<TData> | ColGroupDef<TData>)[] | undefined | null,
): ColDef<TData>[] | undefined =>
  defs?.flatMap((def) =>
    isGroupColDef(def) ? flattenColDefs(def.children) ?? [] : def,
  );

export const tryGetAgGridApi = <T>(
  agGrid: AgGridReact<T> | null | undefined,
) => {
  const api = agGrid?.api;
  if (api?.isDestroyed()) {
    return undefined;
  }
  return api ?? undefined;
};
